// deployment_class: customer_env_allowed
/**
 * Route → Portal mapping. Plan 14 §Task 7 + Spec 12 §4.
 *
 * Pure function: maps a URL path to a portal identifier. Used by:
 *   - AskKathedralOverlay (Task 9) to inject portal context into AI calls
 *   - PortalSwitcher (Task 6) to highlight the current portal
 *   - Cross-portal nav tests (Task 15) to assert context updates
 */

import type { PortalId } from './visible-portals';

export type PortalIdOrUnknown = PortalId | 'unknown';

export function getPortalFromPath(path: string): PortalIdOrUnknown {
  // Normalize: strip query/hash, ensure leading slash.
  const normalized = stripQueryAndHash(path);
  // The Executive cockpit — its own reachable route (admins navigate here from
  // the switcher; CEO-tier land on '/'). Both map to the dashboard portal.
  if (normalized === '/dashboard' || normalized.startsWith('/dashboard/')) {
    return 'dashboard';
  }
  if (
    normalized === '/marketplace' ||
    normalized.startsWith('/marketplace/') ||
    normalized === '/registry' ||
    normalized.startsWith('/registry/')
  ) {
    return 'marketplace';
  }
  if (
    normalized === '/partnerships' ||
    normalized.startsWith('/partnerships/')
  ) {
    return 'partnership';
  }
  if (normalized.startsWith('/admin/sales')) {
    return 'sales';
  }
  if (normalized.startsWith('/admin/data-analytics')) {
    return 'data-analytics';
  }
  return 'unknown';
}

function stripQueryAndHash(path: string): string {
  const qIdx = path.indexOf('?');
  const hIdx = path.indexOf('#');
  let end = path.length;
  if (qIdx >= 0) end = Math.min(end, qIdx);
  if (hIdx >= 0) end = Math.min(end, hIdx);
  return path.slice(0, end);
}

/**
 * Default landing path for a given portal — used by PortalSwitcher when the
 * user clicks a switcher entry.
 */
export function getPortalHomePath(portal: PortalId): string {
  switch (portal) {
    case 'dashboard':
      return '/dashboard';
    case 'marketplace':
      return '/marketplace';
    case 'partnership':
      return '/partnerships';
    case 'sales':
      return '/admin/sales';
    case 'data-analytics':
      return '/admin/data-analytics';
  }
}
